import { useState, createContext, useContext } from 'react'
import { getCompleteMeasurementUnits } from 'utils/dataUnitsDefault'
import { ALL_DAY_PERIOD } from './TimeWindow/timingConstants'
import { getLevelLimits } from './alertLevelLimits'
import { isInvalidDevice } from './deviceValidity'

const AlertFormContext = createContext(null)

// Lets form steps read the shared form data.
export const useAlertForm = () => {
  const ctx = useContext(AlertFormContext)
  if (!ctx) throw new Error('useAlertForm must be used within AlertFormProvider')
  return ctx
}

// Form fields hold strings. An alert being edited arrives with numbers, so the
// values are converted once here. Before, the step read them back from the DOM
// and they were already strings by the time the form was submitted.
const toFormValues = (typesInput = {}) =>
  Object.keys(typesInput).reduce(
    (values, type) => ({
      ...values,
      [type]:
        typesInput[type] === null || typesInput[type] === undefined ? '' : String(typesInput[type])
    }),
    {}
  )

const FORM_CONFIGS = {
  add: {
    submitLabel: 'createAlarm',
    submitErrorMessages: {
      409: 'submitErrors.systemAlreadyInOtherAlarm'
    }
  },
  edit: {
    submitLabel: 'saveChanges',
    submitErrorMessages: {
      404: 'submitErrors.systemNotFound',
      409: 'submitErrors.systemAlreadyInUse'
    }
  }
}

/**
 * initialValues fills the form when an alarm is edited. Every field is
 * optional. Missing fields use the same empty values as a new alarm.
 *
 *   types             string[]  the chosen parameter types
 *   typesInput        object     the alert value for each type
 *   selectedDevices   string[]   the chosen device IDs
 *   existingDeviceIds string[]   devices already used by this alert
 *   alarmName         string     the alarm name
 *   emails            object     selected email addresses
 *   occupancyDays     object     selected days
 *   timing            object     schedule type and local times
 */

// Holds data that is shared between form steps.
//
//   config       read-only data the steps display and validate against
//                (sourced from Redux / i18n; the provider never mutates it)
//   modalActions the modal-level callbacks (recipients + close)
const AlertFormProvider = ({
  children,
  config = {},
  modalActions = {},
  onSubmit,
  initialValues = {},
  formMode = 'add'
}) => {
  const {
    translate,
    user,
    systems,
    systemsByGroup,
    parameterAlarmParamsBySystem,
    isSubmitting,
    isAddingEmail,
    submitError,
    projectId,
    projects
  } = config
  const { addAlertRecipient, onDeleteAlertRecipient, hideModal } = modalActions

  const resolvedFormMode = FORM_CONFIGS[formMode] ? formMode : 'add'
  const formConfig = FORM_CONFIGS[resolvedFormMode]

  // Shared form data.
  const [activeTab, setActiveTab] = useState(0)
  const [typeSelected, setTypeSelected] = useState(initialValues.types ?? [])
  const [selectedDevices, setSelectedDevices] = useState(initialValues.selectedDevices ?? [])
  const [valuesData, setValuesData] = useState(() => toFormValues(initialValues.typesInput))

  // How the values step was filled in. It lives here and not in the step
  // because the step is unmounted while the user is on another one, so its own
  // state would be lost every time the user goes back.
  //
  //   levelType  'Predetermined' when the user picks a preset, 'Personalized'
  //              when the user types the values
  //   level      the chosen preset, or null when none is chosen yet
  const [levelType, setLevelType] = useState('Personalized')
  const [level, setLevel] = useState(null)

  // These values do not change while the modal is open.
  const measurementUnits = getCompleteMeasurementUnits(user.toJS().measurementUnits)
  const offset = new Date().getTimezoneOffset()

  // A device is only offered for the parameters it can measure, and only when
  // it has no alert for them already. Changing the parameters can turn a
  // chosen device into one of those, so the selection is filtered here.
  // Otherwise the device came back checked and disabled, the user could not
  // remove it, and the alert was created for a device without that sensor.
  //
  // Removing a parameter counts too: the support check looks at the first
  // parameter, and a smaller set of parameters is more likely to match an
  // alert the device already has.
  const keepValidDevices = types =>
    setSelectedDevices(previous =>
      previous.filter(deviceId => {
        const system = (systems ?? []).find(item => item._id === deviceId)
        if (!system) return true

        return !isInvalidDevice({
          system,
          typeSelected: types,
          parameterAlarmParamsBySystem,
          existingDeviceIds: initialValues.existingDeviceIds,
          formMode: resolvedFormMode
        })
      })
    )

  const hasSameParameters = types =>
    types.length === typeSelected.length && types.every(type => typeSelected.includes(type))

  // Move to the next or previous step.
  const completeParameters = types => {
    setTypeSelected(types)
    // Only a parameter change can invalidate a device. Filtering when the
    // parameters are the same would drop a device of the alert being edited
    // whose sensor no longer reports the parameter, and the user would lose it
    // without touching anything.
    if (!hasSameParameters(types)) keepValidDevices(types)
    // The preset is kept when the user goes back, so a parameter chosen after
    // it needs its preset value too. Without this its field would be empty and
    // disabled, and the user could not move on.
    if (level)
      setValuesData(previous => ({
        ...previous,
        ...getLevelLimits(level, types, measurementUnits)
      }))
    setActiveTab(1)
  }

  const completeValues = () => setActiveTab(2)

  // The values step writes every change here as it happens, so nothing is lost
  // when the user leaves the step.
  const setAlertValue = (dataType, value) =>
    setValuesData(previous => ({ ...previous, [dataType]: value }))

  // Typing your own values starts from an empty field, so the preset is dropped.
  const chooseLevelType = nextLevelType => {
    if (nextLevelType === 'Personalized') {
      setLevel(null)
      setValuesData(previous =>
        typeSelected.reduce((values, dataType) => ({ ...values, [dataType]: '' }), previous)
      )
    }
    setLevelType(nextLevelType)
  }

  // Picking a preset fills in the value of every parameter in the alert.
  const chooseLevel = nextLevel => {
    setLevel(nextLevel)
    setValuesData(previous => ({
      ...previous,
      ...getLevelLimits(nextLevel, typeSelected, measurementUnits)
    }))
  }

  const completeDevices = () => setActiveTab(3)

  // The devices step writes its selection here as it happens, for the same
  // reason as the values step above. It accepts an updater function, so the
  // step can add or remove devices from the current list.
  const setAlertDevices = devices => setSelectedDevices(devices)

  const goBack = () => setActiveTab(tab => Math.max(0, tab - 1))

  // Combine all step data before submitting the form.
  const submit = settingsData => {
    onSubmit({
      typesInput: valuesData,
      types: typeSelected,
      selectedDevices,
      ...settingsData
    })
  }

  return (
    <AlertFormContext.Provider
      value={{
        state: {
          activeTab,
          typeSelected,
          selectedDevices,
          valuesData,
          levelType,
          level,
          isSubmitting,
          isAddingEmail,
          submitError
        },
        actions: {
          completeParameters,
          completeValues,
          completeDevices,
          setAlertValue,
          chooseLevelType,
          chooseLevel,
          setAlertDevices,
          goBack,
          submit,
          addAlertRecipient,
          onDeleteAlertRecipient,
          hideModal
        },
        meta: {
          translate,
          user,
          systems,
          systemsByGroup,
          parameterAlarmParamsBySystem,
          projectId,
          projects,
          measurementUnits,
          offset,
          ALL_DAY_PERIOD,
          initialValues,
          formMode: resolvedFormMode,
          submitLabel: formConfig.submitLabel,
          submitErrorMessages: formConfig.submitErrorMessages
        }
      }}
    >
      {children}
    </AlertFormContext.Provider>
  )
}

export default AlertFormProvider
